import { isNil, isUndefined } from 'lodash'

// Action Types
export const LOGIN_USER = 'auth/loginUser'
export const LOGOUT_USER = 'auth/logoutUser'

// Action Creators
export const loginUser = (payload) => ({ type: LOGIN_USER, payload })
export const logoutUser = () => ({ type: LOGOUT_USER })

// State
export const initialState = {
  first_name: null,
  last_name: null,
  token: null,
  username: null,
  user_id: null,
  organizationid: null,
  organization_name: null,
  edit_config: null,
  edit_users: null,
  edit_leaderboard: null,
  realtime_coaching_access: null,
  org_realtime_coaching_access: null,
  account_leaderboard_visibility: null,
  account_production: null,
  account_rtqa_access: null,
  edit_qa: null,
  edit_qa_copilot: null,
  playbook_name: null,
  uuid: null,
  hierarchy_manager: null,
  metrics_job_function: null,
  own_organization_id: null,
  tools_editor: null,
  balto_cloud_access: null,
  // null = the server did not send capabilities (an older core-service, or a
  // session stored before they existed); the UI then keeps the legacy flags.
  resolved_permissions: null,
  permissions_status: null,
}

export const PERMISSIONS_STATUS = {
  LOADED: 'loaded',
  FAILED: 'failed',
}

// RBAC capabilities the frontend gates on. Names match the backend catalogue
// (database-models/models/authz/catalogue.py).
export const PERMISSIONS = {
  COACHING_READ: 'coaching:read',
  COMPLIANCE_READ: 'compliance:read',
  CONFIG_WRITE: 'config:write',
  HIERARCHY_WRITE: 'hierarchy:write',
  LEADERBOARD_READ: 'leaderboard:read',
  LEADERBOARD_WRITE: 'leaderboard:write',
  ORGANIZATIONS_WRITE: 'organizations:write',
  OUTBOUND_CAMPAIGNS_WRITE: 'outbound_campaigns:write',
  PLAYLISTS_WRITE: 'playlists:write',
  PROMPTS_WRITE: 'prompts:write',
  QUALITY_READ: 'quality:read',
  QUALITY_WRITE: 'quality:write',
  REALTIME_COACHING_READ: 'realtime_coaching:read',
  REPORTS_READ: 'reports:read',
  SUPPORT_ACCESS_WRITE: 'support_access:write',
  TOGO_WRITE: 'togo:write',
  TOOLS_WRITE: 'tools:write',
  USERS_READ: 'users:read',
  USERS_WRITE: 'users:write',
  WEBHOOKS_WRITE: 'webhooks:write',
}

const RBAC_MODE_ENFORCE = 'enforce'
export const RBAC_MODE_OFF = 'off'

// How the backend gates an authority flag under 'enforce'. Most flags were
// replaced outright by their capability, but some backends still read the raw
// column, and the UI has to match them or it shows controls that 404/403:
// - CAPABILITY_AND_FLAG: the capability, then the column. core-service's user,
//   tag and checklist handlers (edit_users, edit_config) still check it after
//   their capability gate.
// - FLAG: the column alone. balto-auth's hierarchy org reach never moved to
//   hierarchy:write.
const ENFORCE_GATE = {
  CAPABILITY: 'capability',
  CAPABILITY_AND_FLAG: 'capabilityAndFlag',
  FLAG: 'flag',
}

// Every authority flag the UI reads off the current user, with the capability
// that replaced it (transition.py FLAG_TO_PERMISSION, minus the entitlement-ish
// flags that do not gate admin UI) and how the backend gates it under 'enforce'.
// Several flags can share a capability; the first listed is its canonical flag.
const RBAC_FLAG_GATES = {
  edit_config: { permission: PERMISSIONS.CONFIG_WRITE, gate: ENFORCE_GATE.CAPABILITY_AND_FLAG },
  edit_qa: { permission: PERMISSIONS.QUALITY_WRITE, gate: ENFORCE_GATE.CAPABILITY },
  edit_qa_copilot: { permission: PERMISSIONS.QUALITY_WRITE, gate: ENFORCE_GATE.CAPABILITY },
  edit_users: { permission: PERMISSIONS.USERS_WRITE, gate: ENFORCE_GATE.CAPABILITY_AND_FLAG },
  edit_leaderboard: { permission: PERMISSIONS.LEADERBOARD_WRITE, gate: ENFORCE_GATE.CAPABILITY },
  hierarchy_manager: { permission: PERMISSIONS.HIERARCHY_WRITE, gate: ENFORCE_GATE.FLAG },
  manage_support_access: {
    permission: PERMISSIONS.SUPPORT_ACCESS_WRITE,
    gate: ENFORCE_GATE.CAPABILITY,
  },
  outbound_campaign_editor: {
    permission: PERMISSIONS.OUTBOUND_CAMPAIGNS_WRITE,
    gate: ENFORCE_GATE.CAPABILITY,
  },
  prompt_editor: { permission: PERMISSIONS.PROMPTS_WRITE, gate: ENFORCE_GATE.CAPABILITY },
  togo_editor: { permission: PERMISSIONS.TOGO_WRITE, gate: ENFORCE_GATE.CAPABILITY },
  tools_editor: { permission: PERMISSIONS.TOOLS_WRITE, gate: ENFORCE_GATE.CAPABILITY },
  webhook_editor: { permission: PERMISSIONS.WEBHOOKS_WRITE, gate: ENFORCE_GATE.CAPABILITY },
}

export const RBAC_FLAGS = Object.keys(RBAC_FLAG_GATES)

// This session's permissions fetch: never written to localStorage, so a reload
// can't decide on capabilities from an earlier session (a role since changed).
export const SESSION_ONLY_USER_FIELDS = ['permissions_status', 'resolved_permissions']

// The canonical legacy flag each capability replaced. A capability with no entry
// was implied by org membership before RBAC.
const LEGACY_FLAG_FOR_PERMISSION = Object.entries(RBAC_FLAG_GATES).reduce(
  (flagsByPermission, [flag, { permission }]) =>
    flagsByPermission[permission]
      ? flagsByPermission
      : { ...flagsByPermission, [permission]: flag },
  {}
)

// The value a flag should hold under 'enforce', given its gate.
const enforcedFlagValue = (currentUser, flag) => {
  const { permission, gate } = RBAC_FLAG_GATES[flag]
  const heldCapability = currentUser.resolved_permissions.includes(permission)
  const rawFlag = Boolean(currentUser.legacy_flags?.[flag])

  if (gate === ENFORCE_GATE.FLAG) {
    return rawFlag
  }

  return gate === ENFORCE_GATE.CAPABILITY_AND_FLAG ? heldCapability && rawFlag : heldCapability
}

// Selectors

// Whether the backend gates this user on RBAC capabilities. balto_auth applies
// the HOME org's mode (TREN-635), which the permissions response reports as
// `home_rbac_mode` — so a hierarchy manager viewing a child org follows their
// own org's mode, as the backend does. Until that response arrives (or from a
// core-service that predates the field) the mode is read off the viewed org, and
// a hierarchy manager in a child org stays on the legacy flags: never worse than
// the UI before RBAC, and the backend still enforces whatever mode applies.
const isRbacEnforced = (currentUser, currentOrganization = {}) => {
  if (!isNil(currentUser.home_rbac_mode)) {
    return currentUser.home_rbac_mode === RBAC_MODE_ENFORCE
  }

  const viewingChildOrg =
    !isNil(currentUser.own_organization_id) &&
    currentUser.organizationid !== currentUser.own_organization_id

  return !viewingChildOrg && currentOrganization.rbac_mode === RBAC_MODE_ENFORCE
}

// Around sixty components read the authority flags straight off
// `state.currentUser`. Under 'enforce' the backend no longer consults them, so
// a user whose flags say "manager" but whose role is Agent was shown screens
// that 403 on every click. These are the values those flags should hold right
// now: derived from the capabilities under 'enforce', otherwise the flags the
// server last sent (`legacy_flags`), so switching back — a hierarchy manager
// returning from a child org, an org leaving 'enforce' — restores them.
export const deriveRbacFlags = (currentUser, currentOrganization) => {
  if (
    isRbacEnforced(currentUser, currentOrganization) &&
    Array.isArray(currentUser.resolved_permissions)
  ) {
    return Object.fromEntries(
      RBAC_FLAGS.map((flag) => [flag, enforcedFlagValue(currentUser, flag)])
    )
  }

  return currentUser.legacy_flags ?? {}
}

// The UI's single answer to "may this user do X". Under 'enforce' it reads the
// resolved capabilities core-service returns; under 'off' and 'shadow' the
// backend still answers with the legacy gate, so the UI must too, or a shadow
// org would see its menus change before its permissions do.
//
// `legacyAllowed` is the caller's pre-RBAC answer, for gates that never read
// the canonical flag (QA Copilot keys on `edit_qa_copilot`, not `edit_qa`).
// Omitted, it falls back to the flag the capability replaced.
//
// Backwards compatible both ways: without `resolved_permissions` from the server
// (a core-service that predates them, or a failed fetch) the legacy answer
// stands, which is exactly what the UI showed before RBAC existed.
export const hasPermission = (currentUser, currentOrganization, permission, legacyAllowed) => {
  if (
    isRbacEnforced(currentUser, currentOrganization) &&
    Array.isArray(currentUser.resolved_permissions)
  ) {
    // A capability that replaced a flag answers the way its backend gates it:
    // the raw flag alone (hierarchy reach), or the capability and then the
    // column (edit_users, edit_config) — the same value deriveRbacFlags gives
    // the flag, so a control never shows for a call the backend refuses.
    const canonicalFlag = LEGACY_FLAG_FOR_PERMISSION[permission]

    return canonicalFlag
      ? enforcedFlagValue(currentUser, canonicalFlag)
      : currentUser.resolved_permissions.includes(permission)
  }

  if (!isUndefined(legacyAllowed)) {
    return Boolean(legacyAllowed)
  }

  const legacyFlag = LEGACY_FLAG_FOR_PERMISSION[permission]

  return legacyFlag ? Boolean(currentUser[legacyFlag]) : true
}

// Under 'enforce' the login response carries no capabilities; they arrive with
// the permissions fetch. A route guard deciding before then would judge the user
// on legacy flags and could bounce someone their role lets in. Every outcome of
// that fetch sets the status unless the session ended meanwhile, so this cannot
// wait forever. The status is never stored (SESSION_ONLY_USER_FIELDS), so after
// a reload this waits for the new fetch instead of trusting the last session's.
export const arePermissionsPending = (currentUser, currentOrganization) =>
  isRbacEnforced(currentUser, currentOrganization) && !currentUser.permissions_status

// The permissions fetch failed under 'enforce': the UI is running on legacy
// flags, so what it shows may not match what the backend allows.
export const havePermissionsFailed = (currentUser, currentOrganization) =>
  isRbacEnforced(currentUser, currentOrganization) &&
  currentUser.permissions_status === PERMISSIONS_STATUS.FAILED

// Real-Time Coaching product access — the RTC nav item (alert configuration and
// reporting). This is the per-user permission; the live surfaces read
// `hasLiveListenAccess` and the chat surface reads `hasAgentAssistAccess`
// below, neither of which consults it. Hierarchy managers impersonating a child
// org are excluded (org hierarchy isn't supported in RTC yet).
// `real_time_management_access` is the deprecated alias of the user-level
// permission and is still honored.
export const hasRealtimeCoachingAccess = (currentUser) => {
  const {
    realtime_coaching_access: userRealtimeAccess,
    real_time_management_access: deprecatedRealtimeAccess,
    org_realtime_coaching_access: orgRealtimeAccess,
    hierarchy_manager: isHierarchyManager,
    organizationid: currentOrganizationId,
    own_organization_id: ownOrganizationId,
  } = currentUser
  const childOrgSelected = isHierarchyManager && currentOrganizationId !== ownOrganizationId

  return Boolean(
    (userRealtimeAccess || deprecatedRealtimeAccess) && orgRealtimeAccess && !childOrgSelected
  )
}

// The live call surface: the Live Listen page, and inside the Agent Assist
// widget the Listen button and the live transcript. All three are the same
// unredacted call content, so they share one gate.
//
// `showCommandCenter` is not a rollout flag. It is an off switch built for
// Intelogix (orgs 3901/3902), who asked for Live Listen to be disabled BECAUSE
// the live surface is unredacted and they have PCI in scope. So it gates
// content, not a page, and nothing may short-circuit past it — an org that
// bought "no live listen" must not get the same audio and the same words in
// text form through the widget instead.
//
// No per-user permission: it is an org/flag capability, and the page itself
// only redirects on an explicit `false` (`CommandCenterPage`, `IndexRedirect`).
export const hasLiveListenAccess = (currentOrganization = {}, flags = {}) =>
  !currentOrganization.audio_ingestion_only && flags.showCommandCenter !== false

// The chat surface — the Agent Assist widget and its launcher. Chat is
// manager-to-agent text, so it carries none of the call content
// `hasLiveListenAccess` guards and is open to everyone the product reaches.
//
// Deliberately NOT gated on the per-user RTC permission: notification
// subscriptions never were, so a manager sent a raised-hand notification got a
// Chat button wired to a panel that could not mount (QCER-1103). Nor on
// `showCommandCenter`, which would leave that same manager dead-ended in an
// org that only asked to turn off live listen.
//
// Audio-ingestion-only orgs have no live surfaces at all — matching
// `hasRTCAccess` in `Sidebar.helpers.js`.
export const hasAgentAssistAccess = (currentOrganization = {}) =>
  !currentOrganization.audio_ingestion_only

// Kodi entitlement, as resolved by core-service.
//
// `kodi_access` is the ANSWER, not the inputs: core-service computes it with
// the same `is_kodi_enabled` the Kodi service gates on, against the user's HOME
// org. The frontend deliberately does not resolve this itself —
// `users.kodi_enabled` NULL means "inherit the org", and for a hierarchy
// manager the store holds the org being VIEWED rather than the home org whose
// flag governs, so any local resolution disagreed with the server in one
// direction or the other.
//
// Shared by the Kodi nav item and the /kodi guard so the two can't drift — a
// nav item that shows a page which immediately redirects is worse than no nav
// item. Deliberately does NOT include the `baltoCoworkEnabled` LD check: that
// gates Balto's rollout, is read from a different source, and callers combine
// the two themselves.
export const hasKodiEntitlement = (currentUser) => {
  const { kodi_access: kodiAccess } = currentUser

  // undefined means core-service is not serving the field yet, so fail OPEN —
  // failing closed would hide Kodi from every user the moment the web app
  // deploys ahead of the API. Safe because this is only UI: the Kodi service
  // enforces the same entitlement and fails closed.
  if (kodiAccess === undefined) {
    return true
  }

  return Boolean(kodiAccess)
}

// Reducer
export default function currentUserReducer(state = initialState, action) {
  switch (action.type) {
    case LOGIN_USER:
      return action.payload
    case LOGOUT_USER:
      return initialState
    default:
      return state
  }
}
